/**
 * Swatch Color Classification & Sorting Utility
 * 
 * Groups swatches into standardized color categories based on
 * visual analysis of actual swatch imagery (not just names).
 * Applied consistently across Broadloom, Carpet Tile, and Custom Logo Mat products.
 */

export const COLOR_GROUPS = [
  { id: 0, label: "Black" },
  { id: 1, label: "Charcoal / Dark Gray" },
  { id: 2, label: "Gray / Silver" },
  { id: 3, label: "Light Gray / Off-White" },
  { id: 4, label: "Beige / Tan / Cream" },
  { id: 5, label: "Brown" },
  { id: 6, label: "Red / Burgundy" },
  { id: 7, label: "Orange / Rust" },
  { id: 8, label: "Yellow / Gold" },
  { id: 9, label: "Green" },
  { id: 10, label: "Blue" },
  { id: 11, label: "Purple" },
  { id: 12, label: "Multi-color / Patterned" },
];

/**
 * Explicit swatch-to-group mapping based on visual image analysis.
 * Keys are lowercase swatch names.
 */
const EXPLICIT_MAP = {
  // ─── Group 0: Black ───
  "black": 0,
  "black shadow": 0,
  "blackshadow2343": 0,
  "blackstone": 0,
  "black shadow 2343": 0,

  // ─── Group 1: Charcoal / Dark Gray ───
  "charcoal": 1,
  "charcoal1198": 1,
  "carbon": 1,
  "anthracite": 1,
  "anthracite 907": 1,
  "dark grey": 1,
  "zinc": 1,
  "slate": 1,
  "prism": 1,       // Visually: dark base with subtle multi-color undertones
  "northstar": 1,   // Visually: charcoal/dark gray striations
  "nedulae": 1,     // Visually: charcoal/dark gray ribbed
  "jet lag": 1,     // Visually: dark gray vertical stripes
  "sloe": 1,        // Visually: charcoal/dark gray geometric
  "wren": 1,        // Visually: charcoal/dark gray pattern
  "superior nop   gris charcoal 2098": 1,

  // ─── Group 2: Gray / Silver ───
  "sterling": 2,
  "sterling 2344": 2,
  "sterling2344": 2,
  "mid grey": 2,
  "smoke": 2,
  "heather grey": 2,
  "smokey grey": 2,
  "dapple grey": 2,
  "grey": 2,
  "steel": 2,
  "interlapse grey": 2,
  "riverstone": 2,
  "moleskin": 2,     // Visually: mid-tone gray
  "constellation": 2, // Visually: gray/silver striations
  "comet": 2,        // Visually: gray/silver
  "superior nop   stone 2099": 2,

  // ─── Group 3: Light Gray / Off-White ───
  "white": 3,
  "pebble": 3,

  // ─── Group 4: Beige / Tan / Cream ───
  "natural beige": 4,
  "sand": 4,
  "coir natural": 4,
  "coir": 4,
  "taupe": 4,
  "tan": 4,
  "beige": 4,
  "beige 918": 4,
  "natural": 4,
  "sisal": 4,
  "sisal 1099": 4,
  "khaki": 4,
  "solar linen": 4,
  "standard tan": 4,
  "adobe": 4,       // Visually: beige/tan textured

  // ─── Group 5: Brown ───
  "chocolate": 5,
  "chocolate 912": 5,
  "walnut": 5,
  "teak": 5,
  "brown": 5,
  "mink": 5,
  "heath": 5,
  "autumn": 5,       // Visually: dark multi-tonal brown
  "harvest": 5,      // Visually: rich dark brown
  "harvest 1244": 5,
  "stargaze": 5,     // Visually: dark warm brown with striations
  "superior nop   nugget 2097": 5,

  // ─── Group 6: Red / Burgundy ───
  "bright red": 6,
  "cardinal red": 6,
  "cardinal red 3353": 6,
  "crimson": 6,
  "solid crimson": 6,
  "burgundy": 6,
  "garnet": 6,
  "rosewood": 6,
  "bordeaux": 6,
  "bordeaux 7178": 6,
  "red": 6,
  "cranberry": 6,
  "pink": 6,

  // ─── Group 7: Orange / Rust ───
  "orange": 7,

  // ─── Group 8: Yellow / Gold ───
  "gold": 8,
  "yellow": 8,

  // ─── Group 9: Green ───
  "hunter green": 9,
  "kelly green": 9,
  "loden": 9,
  "verdi green": 9,
  "verde green": 9,
  "green": 9,
  "autumn green": 9,
  "olive": 9,
  "aspen": 9,        // Visually: deep dark green
  "aspen 6265": 9,

  // ─── Group 10: Blue ───
  "azure": 10,
  "azure 5181": 10,
  "midnight blue": 10,
  "midnight blue 5154": 10,
  "royal blue": 10,
  "sky blue": 10,
  "steel blue": 10,
  "turquoise": 10,
  "indigo blue": 10,
  "blue": 10,
  "navy": 10,
  "light blue": 10,
  "cosmic blue": 10,
  "brice": 10,       // Visually: dark blue-gray

  // ─── Group 11: Purple ───
  "purple": 11,

  // ─── Group 12: Multi-color / Patterned ───
  "marble": 12,
};

/**
 * Keyword-based fallback classification for swatches
 * not found in the explicit map.
 */
const KEYWORD_GROUPS = [
  { keywords: ["black"], group: 0 },
  { keywords: ["charcoal", "anthracite", "dark gr", "carbon", "slate", "graphite"], group: 1 },
  { keywords: ["grey", "gray", "silver", "sterling", "smoke", "heather", "steel", "pewter", "ash"], group: 2 },
  { keywords: ["white", "ivory", "cream"], group: 3 },
  { keywords: ["beige", "tan", "sand", "coir", "taupe", "sisal", "natural", "khaki", "linen", "adobe", "oat"], group: 4 },
  { keywords: ["brown", "chocolate", "walnut", "teak", "mink", "chestnut", "cocoa", "mocha", "espresso", "umber", "sepia"], group: 5 },
  { keywords: ["red", "crimson", "cardinal", "burgundy", "garnet", "rosewood", "bordeaux", "cranberry", "cherry", "ruby", "pink", "maroon"], group: 6 },
  { keywords: ["orange", "rust", "amber", "copper", "terracotta"], group: 7 },
  { keywords: ["yellow", "gold", "mustard", "honey", "saffron", "nugget"], group: 8 },
  { keywords: ["green", "olive", "loden", "moss", "emerald", "forest", "sage", "aspen", "hunter", "kelly", "verde", "verdi"], group: 9 },
  { keywords: ["blue", "azure", "navy", "midnight", "cobalt", "indigo", "sky", "teal", "turquoise", "sapphire", "royal", "cosmic", "ocean"], group: 10 },
  { keywords: ["purple", "violet", "plum", "lavender", "mauve", "amethyst"], group: 11 },
  { keywords: ["marble", "multi", "pattern", "mixed", "mosaic", "blend"], group: 12 },
];

/**
 * Get the color group index for a swatch by name.
 * 1. Try exact match in explicit map
 * 2. Fall back to keyword matching
 * 3. Default to Multi-color (12) if unclassified
 */
export function getSwatchColorGroup(swatchName) {
  const normalized = swatchName.toLowerCase().trim();

  // 1. Explicit map match
  if (EXPLICIT_MAP[normalized] !== undefined) {
    return EXPLICIT_MAP[normalized];
  }

  // 2. Keyword fallback
  for (const { keywords, group } of KEYWORD_GROUPS) {
    if (keywords.some(kw => normalized.includes(kw))) {
      return group;
    }
  }

  // 3. Default
  return 12;
}

/**
 * Sort swatches by color group order, maintaining relative order within each group.
 * Returns a new array with swatches sorted and annotated with groupId/groupLabel.
 */
export function sortSwatchesByColorGroup(swatches) {
  if (!swatches || swatches.length <= 1) return swatches;

  // Items whose name contains "(Stocked)" are surfaced first — in-stock options
  // sell better and should appear at the top of the swatch grid. Within each
  // tier (stocked / not-stocked) we then sort by color group as before.
  const isStocked = (name) => /\(stocked\)/i.test(name || "");

  return [...swatches]
    .map(swatch => ({
      ...swatch,
      _groupId: getSwatchColorGroup(swatch.name),
      _stockedPriority: isStocked(swatch.name) ? 0 : 1,
    }))
    .sort((a, b) => a._stockedPriority - b._stockedPriority || a._groupId - b._groupId);
}

/**
 * Group swatches into { groupLabel, groupId, swatches[] } sections.
 * Only includes groups that have at least one swatch.
 */
export function groupSwatchesByColor(swatches) {
  if (!swatches || swatches.length === 0) return [];

  const sorted = sortSwatchesByColorGroup(swatches);
  const groups = [];
  let currentGroupId = null;

  for (const swatch of sorted) {
    if (swatch._groupId !== currentGroupId) {
      currentGroupId = swatch._groupId;
      groups.push({
        groupId: currentGroupId,
        groupLabel: COLOR_GROUPS[currentGroupId]?.label || "Other",
        swatches: [],
      });
    }
    groups[groups.length - 1].swatches.push(swatch);
  }

  return groups;
}
